/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * Subscribes to the recipient's own unread access notices.
 */

import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import {
  accessNoticesActions,
  COLLECTIONS,
  firebaseDB,
  shouldMountAccessNotices,
  useAppDispatch,
  useAppSelector,
} from "@lemonadetraining/web-shared";
import { toEpochMs } from "../lib/firestoreTime";

/** Matches the (readAt ASC, createdAt DESC) composite index. */
const UNREAD_LIMIT = 10;

export function useAccessNotices(): void {
  const dispatch = useAppDispatch();
  const uid = useAppSelector((state) => state.user.user?.uid ?? null);
  const claims = useAppSelector((state) => state.user.claims);
  const userInfoData = useAppSelector((state) => state.user.userInfoData);

  // Sticky: a removed coach loses every role before the notice explaining it
  // arrives, so the gate only ever arms the listener. It is deliberately not an
  // effect dependency — going false must not tear the subscription down.
  const [armedUID, setArmedUID] = useState<string | null>(null);
  const shouldMount = shouldMountAccessNotices(claims, userInfoData);

  useEffect(() => {
    if (uid && shouldMount && armedUID !== uid) setArmedUID(uid);
  }, [uid, shouldMount, armedUID]);

  useEffect(() => {
    if (!uid || armedUID !== uid) return undefined;

    const unsubscribe = onSnapshot(
      query(
        collection(firebaseDB, COLLECTIONS.Users, uid, COLLECTIONS.AccessNotices),
        where("readAt", "==", null),
        orderBy("createdAt", "desc"),
        limit(UNREAD_LIMIT)
      ),
      (snapshot) => {
        dispatch(
          accessNoticesActions.setUnread(
            snapshot.docs.map((document) => {
              const data = document.data();
              return { ...data, id: document.id, createdAt: toEpochMs(data.createdAt) };
            })
          )
        );
      },
      (error) => {
        // Code only: the bodies carry an athlete's first name.
        console.warn("Access notices listener failed:", error?.code ?? "unknown");
        dispatch(accessNoticesActions.setListenerFailed(true));
      }
    );

    return () => unsubscribe();
  }, [uid, armedUID, dispatch]);
}
